import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Siren, VolumeX } from "lucide-react";
import { subscribePulse } from "../utils/pulse";
import useFeatures from "../hooks/useFeatures";
import { playEmergencyAlarm, stopEmergencyAlarm } from "../utils/notificationSound";

// Red ribbon before the bells while a crisis alert sent to this user is
// still waiting for someone to tap "I'm on it" (the "crisis" part of the
// shared poll, utils/pulse.js). The siren sounds when a new one arrives and again
// every 30 seconds until someone acknowledges it or this user mutes it.
//
// The layout renders two copies (desktop, and the mobile header hidden by
// CSS) and every page mounts its own layout, so polling, the siren and the
// mute list live here at module level - one shared store for every copy -
// and the mute list is kept for the browser session.
const REPEAT_MS = 30000;
const MUTE_KEY = "crisis_alerts_muted";

const store = { waiting: [], listeners: new Set(), unsub: null, lastAlarm: 0, muted: new Set() };
try { JSON.parse(sessionStorage.getItem(MUTE_KEY) || "[]").forEach((id) => store.muted.add(id)); } catch { /* none */ }

const emit = () => store.listeners.forEach((fn) => fn(store.waiting));
const loudCount = () => store.waiting.filter((a) => !store.muted.has(a.id)).length;

// Fed by the shared background poll (utils/pulse.js, GET /pulse "crisis").
function onPulse(p) {
  if (!Array.isArray(p.crisis)) return;
  store.waiting = p.crisis.filter((a) => a.status === "open");
  emit();
  // The poll runs every ~30s, so allow some slack or the siren would skip a round.
  if (loudCount() && Date.now() - store.lastAlarm >= REPEAT_MS - 5000) {
    store.lastAlarm = Date.now();
    playEmergencyAlarm();
  }
  if (!loudCount()) stopEmergencyAlarm();
}

function subscribe(fn) {
  store.listeners.add(fn);
  fn(store.waiting);
  if (!store.unsub) store.unsub = subscribePulse(onPulse);
  return () => {
    store.listeners.delete(fn);
    if (store.listeners.size === 0) { store.unsub(); store.unsub = null; }
  };
}

function muteAll() {
  store.waiting.forEach((a) => store.muted.add(a.id));
  try { sessionStorage.setItem(MUTE_KEY, JSON.stringify([...store.muted])); } catch { /* private mode */ }
  stopEmergencyAlarm();
  emit();
}

export default function CrisisRibbon({ compact = false }) {
  const features = useFeatures();
  const navigate = useNavigate();
  const location = useLocation();
  const [waiting, setWaiting] = useState(store.waiting);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!features.has("crisis")) return undefined;
    return subscribe((list) => setWaiting([...list]));
  }, [features.ready]); // eslint-disable-line

  if (waiting.length === 0) return null;
  const first = waiting[0];
  const loud = waiting.some((a) => !store.muted.has(a.id));
  const patients = [...new Set(waiting.map((a) => a.patient_id))];
  const label = waiting.length > 1
    ? `${waiting.length} crisis alerts${patients.length === 1 && first.patient_name ? `: ${first.patient_name}` : ""}`
    : `Crisis alert${first.patient_name ? `: ${first.patient_name}` : ""}`;
  const fmt = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" }) : "");
  const go = (pid) => {
    setMenuOpen(false);
    const target = `/patient/${pid}`;
    if (location.pathname === target) {
      // Already on this record: bring the alert into view and reload it.
      window.scrollTo({ top: 0, behavior: "smooth" });
      window.dispatchEvent(new CustomEvent("crisis-alerts-changed", { detail: { patientId: pid } }));
    } else {
      navigate(target);
    }
  };

  return (
    <div className="relative">
      <div className="relative flex items-center rounded-full bg-rose-600 text-white shadow-lg shadow-rose-600/40" role="alert">
        {loud && <span className="absolute inset-0 rounded-full ring-4 ring-rose-500/60 animate-ping pointer-events-none" aria-hidden="true" />}
        <button type="button" onClick={() => (patients.length === 1 ? go(first.patient_id) : setMenuOpen((o) => !o))}
          aria-expanded={patients.length > 1 ? menuOpen : undefined}
          className={`relative inline-flex items-center gap-1.5 font-bold ${compact ? "px-2.5 py-1.5 text-xs" : "px-3.5 py-2 text-sm"}`}
          title={`${first.raised_by_patient ? "Patient asked for help" : `Raised by ${first.raised_by_name || "staff"}`}${first.summary ? ` - ${first.summary}` : ""}`}>
          <Siren className="w-4 h-4" />
          {compact ? (waiting.length > 1 ? waiting.length : "Crisis") : <>{label} · <span className="underline">Open</span></>}
        </button>
        {loud && (
          <button type="button" aria-label="Mute alarm" title="Mute alarm" onClick={muteAll}
            className="relative mr-1 p-1.5 rounded-full hover:bg-white/20">
            <VolumeX className="w-4 h-4" />
          </button>
        )}
      </div>
      {menuOpen && patients.length > 1 && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] rounded-xl bg-white shadow-2xl border border-rose-100 overflow-hidden z-50">
          <p className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-rose-700 bg-rose-50">Waiting for someone to respond</p>
          {waiting.map((a) => (
            <button key={a.id} type="button" onClick={() => go(a.patient_id)} className="w-full text-left px-4 py-3 border-t border-gray-100 hover:bg-rose-50">
              <p className="text-sm font-bold text-gray-900">{a.patient_name || "Patient"} <span className="font-normal text-gray-500">· {fmt(a.created_at)}</span></p>
              <p className="text-xs font-semibold text-rose-700">{a.raised_by_patient ? "Patient asked for help" : `Raised by ${a.raised_by_name || "staff"}`}</p>
              {a.summary && <p className="text-xs text-gray-600 line-clamp-2">{a.summary}</p>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
